import { useEffect, useState } from "react";
import AdminListToolkit from "../../components/listing/AdminListToolkit";
import AdminDataModal from "../../components/AdminDataModal";
import type { FormField } from "../../components/AdminDataModal";
import { useMessageModal } from "../../contexts/MessageModalContext";

export default function PaymentGateways() {
  const BASE_URL = import.meta.env.VITE_LYRAZONE_BASE_URL;

  const { showModal } = useMessageModal();

  const [data, setData] = useState<any[]>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit" | "view">("add");
  const [activeRow, setActiveRow] = useState<any>(null);

  /* =========================
     FETCH PAYMENT GATEWAYS
  ========================= */

  const fetchGateways = async () => {
    try {
      const res = await fetch(`${BASE_URL}/admin/payment-gateways`, {
        credentials: "include",
      });

      const result = await res.json();

      if (result.success) {
        setData(result.data);
      }
    } catch (error) {
      console.error("Fetch Payment Gateways Error:", error);
    }
  };

  useEffect(() => {
    fetchGateways();
  }, []);

  /* =========================
     ADD / UPDATE
  ========================= */

  const handleSubmit = async (formData: any) => {
    try {
      const method = modalMode === "add" ? "POST" : "PUT";

      const url =
        modalMode === "add"
          ? `${BASE_URL}/admin/payment-gateways`
          : `${BASE_URL}/admin/payment-gateways/${activeRow.id}`;

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData),
      });

      const result = await res.json();

      if (result.success) {
        fetchGateways();
        setModalOpen(false);
        setActiveRow(null);
      }
    } catch (error) {
      console.error("Submit Error:", error);
    }
  };

  /* =========================
     DELETE (SOFT DELETE)
  ========================= */

  const handleDelete = (row: any) => {
    showModal({
      type: "confirm",
      title: "Delete Payment Gateway",
      message: `Are you sure you want to delete "${row.name}"?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          const res = await fetch(
            `${BASE_URL}/admin/payment-gateways/${row.id}`,
            {
              method: "DELETE",
              credentials: "include",
            }
          );

          const result = await res.json();

          if (result.success) {
            fetchGateways();
            setSelectedRow(null);

            showModal({
              type: "success",
              title: "Deleted",
              message: "Payment gateway deleted successfully.",
              confirmText: "OK",
            });
          }
        } catch (error) {
          console.error("Delete Error:", error);
        }
      },
    });
  };

  /* =========================
     FORM FIELDS
  ========================= */

  const gatewayFields: FormField[] = [
    { key: "id", label: "ID", colSpan: 2 },
    { key: "name", label: "Gateway Name", colSpan: 2 },
    { key: "provider", label: "Provider", colSpan: 2 },
    { key: "api_key", label: "API Key", colSpan: 2 },
    { key: "secret_key", label: "Secret Key", colSpan: 2 },
    { key: "status", label: "Status", colSpan: 2 },
  ];

  const columns = [
    { key: "name", label: "Gateway Name" },
    { key: "provider", label: "Provider" },
    { key: "status", label: "Status" },
  ];

  return (
    <div>
      <AdminListToolkit
        title="Payment Gateways"
        columns={columns}
        data={data}
        selectedRow={selectedRow}
        onAdd={() => {
          setModalMode("add");
          setActiveRow(null);
          setModalOpen(true);
        }}
        onView={(row) => {
          setModalMode("view");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onEdit={(row) => {
          setModalMode("edit");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onSoftDelete={handleDelete}
        enableExport={false}
      >
        {(filteredData) => (
          <div className="algorithm-grid">
            {filteredData.map((item: any) => (
              <div
                key={item.id}
                className={`algorithm-card ${
                  selectedRow?.id === item.id ? "selected" : ""
                }`}
                onClick={() => setSelectedRow(item)}
              >
                <h3>{item.name}</h3>

                <p>
                  <strong>Provider:</strong> {item.provider}
                </p>

                <p>
                  <strong>Status:</strong>{" "}
                  <span
                    className={
                      item.status === "active"
                        ? "badge-success"
                        : "badge-danger"
                    }
                  >
                    {item.status}
                  </span>
                </p>
              </div>
            ))}
          </div>
        )}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={modalOpen}
        mode={modalMode}
        title="Payment Gateway"
        fields={gatewayFields}
        rowData={activeRow}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
      />
    </div>
  );
}